import { ThemeOptions } from '@mui/material';

// Create spacing functions based on the base unit
const createSpacing = (baseUnit: number) => {
  return (factor: number) => `${baseUnit * factor}px`;
};

export const sizeVariants = {
  'extra small': {
    typography: {
      h1: { fontSize: '1.75rem' },
      h2: { fontSize: '1.4rem' },
      h3: { fontSize: '1.2rem' },
      h4: { fontSize: '1rem' },
      h5: { fontSize: '0.875rem' },
      h6: { fontSize: '0.75rem' },
      body1: { fontSize: '0.75rem' },
      body2: { fontSize: '0.625rem' },
      button: { fontSize: '0.75rem' },
    },
    spacing: createSpacing(4), // 4px base spacing unit
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            padding: '4px 8px',
            minHeight: '28px',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            padding: '8px',
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '40px',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: '4px',
          },
        },
      },
    },
  },
  small: {
    typography: {
      h1: { fontSize: '2rem' },
      h2: { fontSize: '1.6rem' },
      h3: { fontSize: '1.4rem' },
      h4: { fontSize: '1.2rem' },
      h5: { fontSize: '1rem' },
      h6: { fontSize: '0.875rem' },
      body1: { fontSize: '0.875rem' },
      body2: { fontSize: '0.75rem' },
      button: { fontSize: '0.875rem' },
    },
    spacing: createSpacing(6), // 6px base spacing unit
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            padding: '6px 12px',
            minHeight: '32px',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            padding: '12px',
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '48px',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: '6px',
          },
        },
      },
    },
  },
  medium: {
    typography: {
      h1: { fontSize: '2.5rem' },
      h2: { fontSize: '2rem' },
      h3: { fontSize: '1.75rem' },
      h4: { fontSize: '1.5rem' },
      h5: { fontSize: '1.25rem' },
      h6: { fontSize: '1rem' },
      body1: { fontSize: '1rem' },
      body2: { fontSize: '0.875rem' },
      button: { fontSize: '1rem' },
    },
    spacing: createSpacing(8), // 8px base spacing unit
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            padding: '8px 16px',
            minHeight: '40px',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            padding: '16px',
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '64px',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: '8px',
          },
        },
      },
    },
  },
  large: {
    typography: {
      h1: { fontSize: '3rem' },
      h2: { fontSize: '2.4rem' },
      h3: { fontSize: '2.1rem' },
      h4: { fontSize: '1.8rem' },
      h5: { fontSize: '1.5rem' },
      h6: { fontSize: '1.2rem' },
      body1: { fontSize: '1.125rem' },
      body2: { fontSize: '1rem' },
      button: { fontSize: '1.125rem' },
    },
    spacing: createSpacing(10), // 10px base spacing unit
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            padding: '10px 20px',
            minHeight: '48px',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            padding: '20px',
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '80px',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: '10px',
          },
        },
      },
    },
  },
};

export const applySizeVariant = (baseTheme: ThemeOptions, size: 'extra small' | 'small' | 'medium' | 'large'): ThemeOptions => {
  const variant = sizeVariants[size];
  
  return {
    ...baseTheme,
    spacing: variant.spacing,
    typography: {
      ...baseTheme.typography,
      ...variant.typography,
    },
    components: {
      ...baseTheme.components,
      ...variant.components,
    },
  };
}; 